// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PlayCircle from "@insite/mobius/Icons/PlayCircle";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const enum fields {
    videoEmbedCode = "videoEmbedCode",
}

interface YoutubeVideoProps extends WidgetProps {
    fields: {
        [fields.videoEmbedCode]: string;
    };
}

const YoutubeVideoWrapper = styled.div`
    position: relative;
    padding-bottom: 56.25%;
    padding-top: 0;
    height: 0;
    overflow: hidden;

    iframe,
    object,
    embed {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
`;

type YoutubeVideoBgProps = {
    bgUrl: string;
    playVideo: boolean;
};

const YoutubeVideoBg = styled.div<YoutubeVideoBgProps>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("${props => props.bgUrl}");
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center center;
    cursor: pointer;
    display: ${props => (props.playVideo ? "none" : "block")};

    &:before {
        content: " ";
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        background: ${getColor("secondary.main")};
        opacity: 0.25;
        transition: all 0.15s linear;
    }

    .play-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: scale(1) translate(-50%, -50%);
        width: 60px;
        height: 60px;
        transition: all 0.15s linear;
        
        > svg {
            font-size: 20px;
            color: ${getColor("common.white")};
            width: 100%;
            height: 100%;
        }
    }
    
    &:hover,
    &:focus {
        &:before {
            opacity: 0.4;
        }
        
        .play-icon {
            transform: scale(1.2) translate(-50%, -50%);
        }
    }
`;

const youtubeVideoTemplateWidget: React.FC<YoutubeVideoProps> = ({ fields }) => {
    const [bgUrl, setBgUrl] = useState("");
    const [playVideo, setPlayVideo] = useState(false);
    const youtubeVideoIframeRef = useRef<any>(null);

    const getYoutubeVideoIframe = (src?: boolean) => {
        const youtubeVideoIframe = youtubeVideoIframeRef.current?.childNodes[0];

        if (youtubeVideoIframe) {
            return src ? youtubeVideoIframe.src : youtubeVideoIframe;
        }
    };

    const handleYoutubeVideoBgClick = () => {
        const youtubeVideoIframe = getYoutubeVideoIframe();

        if (youtubeVideoIframe) {
            const src = youtubeVideoIframe.src;

            if (src.indexOf("?") > -1) {
                youtubeVideoIframe.src = `${src}&autoplay=1`;
            } else {
                youtubeVideoIframe.src = `${src}?autoplay=1`;
            }

            setPlayVideo(true);
        }
    };

    useEffect(() => {
        const src = getYoutubeVideoIframe(true);

        if (src) {
            const videoId = src.match(/[\w\-]{11,}/)[0];

            if (videoId) {
                setBgUrl(`http://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
            }
        }
    });

    return (
        <YoutubeVideoWrapper>
            <StyledWrapper ref={youtubeVideoIframeRef} dangerouslySetInnerHTML={{ __html: fields.videoEmbedCode }} />
            <YoutubeVideoBg bgUrl={bgUrl} playVideo={playVideo} onClick={handleYoutubeVideoBgClick}>
                <StyledWrapper className="play-icon">
                    <PlayCircle />
                </StyledWrapper>
            </YoutubeVideoBg>
        </YoutubeVideoWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.videoEmbedCode,
            displayName: "Video Embed Code",
            editorTemplate: "MultilineTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: youtubeVideoTemplateWidget,
    definition,
};

export default widgetModule;
